import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

import { cn } from "@app//lib/utils";

/**
 * Tabs over Radix (`@radix-ui/react-tabs` was installed and unused). Controlled
 * or uncontrolled like the primitive; pages that want the active tab in the URL
 * pass `value` / `onValueChange`. A trigger may carry a count badge.
 */
const Tabs = TabsPrimitive.Root;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn("flex items-end gap-1 border-b border-gray-200", className)}
    {...props}
  />
));
TabsList.displayName = "TabsList";

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger> & { count?: number | null }
>(({ className, children, count, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      "-mb-px inline-flex items-center gap-2 border-b-2 border-transparent px-3 py-2 text-sm text-gray-500 transition-colors",
      "hover:text-gray-800 data-[state=active]:border-indigo-600 data-[state=active]:text-gray-900 data-[state=active]:font-medium",
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-indigo-300 disabled:pointer-events-none disabled:opacity-50",
      className,
    )}
    {...props}
  >
    {children}
    {count != null && (
      <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] tabular-nums text-gray-600">
        {count.toLocaleString()}
      </span>
    )}
  </TabsPrimitive.Trigger>
));
TabsTrigger.displayName = "TabsTrigger";

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content ref={ref} className={cn("pt-4 focus-visible:outline-none", className)} {...props} />
));
TabsContent.displayName = "TabsContent";

export { Tabs, TabsList, TabsTrigger, TabsContent };
